import React from "react";
import { createPortal } from "react-dom";

import { PanelBaseContext } from "./PanelBaseContext";

import { useKeyPress } from "../../../hooks/useKeyPress";
import { useOverlayAnimation } from "../../../hooks/useOverlayAnimation";
import { useOverlayFocus } from "../../../hooks/useOverlayFocus";
import { usePortal } from "../../../hooks/usePortal";

import { cn } from "../../../lib/utils";

export type { ExtendedPanelBaseProps } from "./ExtendedPanelBase";
export { ExtendedPanelBase } from "./ExtendedPanelBase";

export type PanelBaseProps = {
	/**
	 * React node content to display inside the panel
	 */
	children: React.ReactNode;
	/**
	 * Whether the panel is open or closed
	 */
	isOpen: boolean;
	/**
	 * Callback function executed when the panel should be closed
	 */
	onClose: () => void;
	/**
	 * Which side of the screen the panel should appear on
	 * @default "left"
	 */
	side?: "left" | "right";
	/**
	 * Optional CSS classes to apply to the panel container (use with caution)
	 */
	dangerouslySetClassName?: string;
	/**
	 * Width of the panel. Accepts any valid CSS width value.
	 * @default "420px"
	 */
	width?: string;
	/**
	 * Content for a secondary panel adjacent to the primary panel.
	 * Use the ExtendedPanelBase component here for built-in width animation and border handling.
	 */
	extendedPanel?: React.ReactNode;
};

/**
 * PanelBase component that provides a sliding panel with portal rendering.
 *
 * This component serves as an unstyled foundation for building panel templates.
 * It includes essential functionality like:
 * - Portal-based rendering outside the DOM hierarchy
 * - Left or right-side slide animations using Tailwind CSS
 * - Background scroll prevention when open
 * - Close on outside click behavior
 * - Close on Escape key press
 * - Customizable width via the `width` prop (defaults to 420px)
 * - Optional extended panel slot for stacked/side-by-side layouts
 *
 * Accessibility features:
 * - Uses appropriate ARIA attributes for modal behavior
 * - Traps focus within the panel when open
 * - Supports keyboard navigation (Escape to close)
 * - Prevents background interaction when open
 */
const PanelBase = (props: PanelBaseProps): React.ReactElement | null => {
	const { children, isOpen, onClose, side = "left", dangerouslySetClassName, width = "420px", extendedPanel } = props;

	const { shouldRender, isAnimating, animatedElementRef } = useOverlayAnimation<HTMLDivElement>({
		isOpen,
		transitionProperty: "transform",
	});

	const { portalRef, isRendered, PortalProvider } = usePortal({
		shouldRender,
		portalAttribute: "data-panel-base-portal",
		preventBodyScroll: true,
	});

	useOverlayFocus<HTMLDivElement>({
		isOpen,
		shouldRender,
		isRendered,
		elementRef: animatedElementRef,
	});

	useKeyPress({
		key: "Escape",
		onPress: (e) => {
			if (!e.defaultPrevented) {
				onClose();
			}
		},
		disabled: !isOpen,
	});

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	if (!isRendered || !portalRef.current) {
		return null;
	}

	return createPortal(
		<PortalProvider>
			<PanelBaseContext.Provider value={{ side }}>
				<div
					className={cn("fixed inset-0 z-[110] flex", side === "right" ? "justify-end" : "")}
					role="dialog"
					aria-modal="true"
					aria-labelledby="panel-content"
				>
					<div
						className={cn(
							"fixed inset-0 bg-black/50 transition-opacity duration-300 ease-in-out",
							isAnimating ? "opacity-100" : "opacity-0"
						)}
						onClick={handleBackdropClick}
						aria-hidden="true"
					/>

					<div
						ref={animatedElementRef}
						className={cn(
							"relative z-10 flex h-full",
							"transform transition-transform duration-300 ease-in-out",
							isAnimating ? "translate-x-0" : side === "right" ? "translate-x-full" : "-translate-x-full"
						)}
						tabIndex={-1}
						id="panel-content"
					>
						{side === "left" && extendedPanel}
						<div
							className={cn(
								"flex h-full flex-shrink-0 flex-col bg-white shadow-xl",
								dangerouslySetClassName
							)}
							style={{ width }}
						>
							{children}
						</div>
						{side === "right" && extendedPanel}
					</div>
				</div>
			</PanelBaseContext.Provider>
		</PortalProvider>,
		portalRef.current
	);
};

PanelBase.displayName = "PanelBase";

export { PanelBase };
